import { useState } from "react";

const ZAPIER_WEBHOOK = "https://hooks.zapier.com/hooks/catch/17916584/uhubf7p/";

function LeadGate({ onComplete, onCancel, calc, enabled = true }) {
  const [step, setStep] = useState(1);
  const [goal, setGoal] = useState("");
  const [timeframe, setTimeframe] = useState("");
  const [name, setName] = useState(localStorage.getItem("leadName") || "");
  const [mobile, setMobile] = useState(localStorage.getItem("leadPhone") || "");
  const [email, setEmail] = useState(localStorage.getItem("leadEmail") || "");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!enabled) return null;

  async function submitToZapier(payload) {
    try {
      setSubmitting(true);
      setError(null);

      const res = await fetch(ZAPIER_WEBHOOK, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        throw new Error(`Zapier error: ${res.status}`);
      }

      console.log("✅ Sent to Zapier", payload);
    } catch (err) {
      console.error("❌ Zapier submission failed", err);
      setError("Something went wrong while submitting. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleSubmit() {
    const payload = { name, email, mobile, consent, goal, timeframe, calc };

    // Save in cache for next time
    localStorage.setItem("leadName", name);
    localStorage.setItem("leadPhone", mobile);
    localStorage.setItem("leadEmail", email);

    submitToZapier(payload);
    onComplete?.(payload);
  }

  const ui = {
    backdrop: {
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "grid", placeItems: "center", zIndex: 9999
    },
    card: {
      width: "min(500px, 94vw)",
      background: "#F2F3F3",
      borderRadius: 8,
      padding: "24px 28px",
      fontFamily: "Calibri, sans-serif"
    },
    title: { fontSize: 22, fontWeight: 700, marginBottom: 12, color: "#141414" },
    label: { fontSize: 13, fontWeight: 600, margin: "18px 0 6px", color: "#141414" },
    input: {
      width: "100%",
      border: "none",
      borderBottom: "1px solid #ccc",
      padding: "10px 2px",
      fontSize: 15,
      outline: "none",
      background: "transparent",
      transition: "background 0.2s"
    },
    btnPrimary: {
      background: "#141414",
      color: "#fff",
      padding: "14px 18px",
      fontWeight: 700,
      fontSize: 15,
      border: "none",
      borderRadius: 2,
      cursor: "pointer",
      marginTop: 20,
      width: "100%"
    },
    btnSecondary: {
      background: "transparent",
      border: "1px solid #141414",
      color: "#141414",
      padding: "12px 16px",
      fontWeight: 600,
      fontSize: 14,
      cursor: "pointer",
      marginTop: 12
    },
    chipRow: { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 },
    chip: {
      padding: "10px 14px",
      borderRadius: 4,
      border: "1px solid #ccc",
      background: "#E5E5E5",
      color: "#333",
      fontWeight: 600,
      cursor: "pointer"
    },
    chipSelected: {
      border: "2px solid #4EDCF6",
      background: "#4EDCF6",
      color: "#141414"
    },
    privacy: {
      fontSize: 12,
      marginTop: 16,
      display: "flex",
      alignItems: "center",
      gap: 6,
      color: "#444"
    },
    error: { color: "#b00020", fontSize: 13, marginTop: 10, fontWeight: 600 }
  };

  return (
    <div style={ui.backdrop}>
      <div style={ui.card}>
        <h3 style={ui.title}>See your borrowing power</h3>

        {step === 1 && (
          <>
            <label style={ui.label}>What are you hoping to do?</label>
            <div style={ui.chipRow}>
              {["Get pre-approval", "Check my borrowing power", "Compare lenders"].map((g) => (
                <button
                  key={g}
                  style={{ ...ui.chip, ...(goal === g ? ui.chipSelected : {}) }}
                  onClick={() => setGoal(g)}
                >
                  {g}
                </button>
              ))}
            </div>

            <label style={ui.label}>When are you looking to buy?</label>
            <div style={ui.chipRow}>
              {["ASAP (0–3 months)", "3–6 months", "6+ months / just researching"].map((t) => (
                <button
                  key={t}
                  style={{ ...ui.chip, ...(timeframe === t ? ui.chipSelected : {}) }}
                  onClick={() => setTimeframe(t)}
                >
                  {t}
                </button>
              ))}
            </div>

            <button
              style={ui.btnPrimary}
              disabled={!goal || !timeframe}
              onClick={() => setStep(2)}
            >
              Next →
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <label style={ui.label}>Full name *</label>
            <input
              style={ui.input}
              onFocus={(e) => (e.target.style.background = "#fff")}
              onBlur={(e) => (e.target.style.background = "transparent")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
            />

            <label style={ui.label}>Email *</label>
            <input
              style={ui.input}
              onFocus={(e) => (e.target.style.background = "#fff")}
              onBlur={(e) => (e.target.style.background = "transparent")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@email.com"
            />

            <label style={ui.label}>Phone *</label>
            <input
              style={ui.input}
              onFocus={(e) => (e.target.style.background = "#fff")}
              onBlur={(e) => (e.target.style.background = "transparent")}
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="04xx xxx xxx"
            />

            <label style={{ fontSize: 13, marginTop: 12 }}>
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                style={{ marginRight: 6 }}
              />
              By continuing, you agree we may contact you about your home loan options.
            </label>

            {error && <div style={ui.error}>{error}</div>}

            <button style={ui.btnSecondary} onClick={() => setStep(1)}>← Back</button>
            <button
              style={ui.btnPrimary}
              disabled={submitting}
              onClick={handleSubmit}
            >
              {submitting ? "Submitting…" : "Check borrowing capacity →"}
            </button>

            <div style={ui.privacy}>
              <span>🔒 Secure submission.</span>
              <a
                href="https://www.mortgagematrix.com.au/privacy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#141414", textDecoration: "underline" }}
              >
                View our Privacy Policy
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LeadGate;
